import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import InteractionHints from './InteractionHints';
import { applyFabricTexture, updateTextureZoom } from './fabricTexture';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const NORM = 125;
const DEFAULT_CAM = { x: 0, y: 0, z: 1.0 };

function buildPillowGeo(w, h) {
  if (w <= 0.001 || h <= 0.001) {
    w = Math.max(w, 0.01);
    h = Math.max(h, 0.01);
  }
  const depth = Math.min(w, h) * 0.32;
  const hw = w / 2, hh = h / 2, hd = depth / 2;

  const segsLon = 64;
  const segsLat = 40;
  const e1 = 0.75;
  const e2 = 0.28;
  const concavity = 0.18;

  const sp = (base, exp) => {
    const a = Math.abs(base);
    if (a < 1e-8) return 0;
    return Math.sign(base) * Math.pow(a, exp);
  };

  // Ruffle waves for organic edge variation
  const ruffleXY = [
    { f: 3, a: 0.30, p: 1.2 },
    { f: 5, a: 0.20, p: 2.7 },
    { f: 7, a: 0.14, p: 0.5 },
    { f: 11, a: 0.10, p: 3.1 },
    { f: 13, a: 0.06, p: 4.6 },
  ];
  const ruffleZ = [
    { f: 4, a: 0.25, p: 0.8 },
    { f: 6, a: 0.20, p: 3.2 },
    { f: 9, a: 0.15, p: 1.5 },
    { f: 14, a: 0.10, p: 2.1 },
  ];
  const ruffleScale = Math.min(w, h) * 0.025;

  const positions = [];
  const uvs = [];

  for (let j = 0; j <= segsLat; j++) {
    const eta = -Math.PI / 2 + (j / segsLat) * Math.PI;
    const cE = Math.cos(eta), sE = Math.sin(eta);
    const edgeFactor = Math.pow(Math.abs(cE), 4);

    for (let i = 0; i <= segsLon; i++) {
      const omega = -Math.PI + (i / segsLon) * 2 * Math.PI;
      const cO = Math.cos(omega), sO = Math.sin(omega);

      let x = hw * sp(cE, e1) * sp(cO, e2);
      let y = hh * sp(cE, e1) * sp(sO, e2);
      let z = hd * sp(sE, e1);

      // Concavity: cos(4*omega) = 1 at edge midpoints, -1 at corners
      const midFactor = (1 + Math.cos(4 * omega)) / 2;
      const cFactor = 1 - concavity * midFactor;
      x *= cFactor;
      y *= cFactor;

      // Thin the corners: reduce depth near corner directions, only at seam edges
      const cornerFactor = (1 - Math.cos(4 * omega)) / 2;
      z *= 1 - 0.4 * cornerFactor * Math.pow(Math.abs(cE), 2);

      // Ruffle displacement at seam edges
      let rxy = 0;
      for (const wave of ruffleXY) rxy += wave.a * Math.sin(omega * wave.f + wave.p);
      let rz = 0;
      for (const wave of ruffleZ) rz += wave.a * Math.sin(omega * wave.f + wave.p);

      x += edgeFactor * rxy * ruffleScale * Math.cos(omega);
      y += edgeFactor * rxy * ruffleScale * Math.sin(omega);
      z += edgeFactor * rz * ruffleScale * 0.4;

      positions.push(x, y, z);
      // World-space UV: tile at consistent physical scale across all faces
      const TILE_INV = 4; // 1 / 0.25m = 4 → one pattern tile per 25cm
      uvs.push(x * TILE_INV, y * TILE_INV);
    }
  }

  const indices = [];
  for (let j = 0; j < segsLat; j++) {
    for (let i = 0; i < segsLon; i++) {
      const a = j * (segsLon + 1) + i;
      const b = a + 1;
      const c = a + segsLon + 1;
      const d = c + 1;
      indices.push(a, b, d);
      indices.push(a, d, c);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

// Compute exact surface position on the bottom edge seam
function bottomEdgePos(omega, w, h) {
  const hw = w / 2, hh = h / 2;
  const e2 = 0.28, concavity = 0.18;
  const sp = (base, exp) => {
    const a = Math.abs(base);
    if (a < 1e-8) return 0;
    return Math.sign(base) * Math.pow(a, exp);
  };
  const cO = Math.cos(omega), sO = Math.sin(omega);
  let x = hw * sp(cO, e2);
  let y = hh * sp(sO, e2);
  let z = 0;
  const midFactor = (1 + Math.cos(4 * omega)) / 2;
  const cFactor = 1 - concavity * midFactor;
  x *= cFactor;
  y *= cFactor;
  const rScale = Math.min(w, h) * 0.025;
  const waves = [
    { f: 3, a: 0.30, p: 1.2 }, { f: 5, a: 0.20, p: 2.7 },
    { f: 7, a: 0.14, p: 0.5 }, { f: 11, a: 0.10, p: 3.1 }, { f: 13, a: 0.06, p: 4.6 },
  ];
  const wZ = [
    { f: 4, a: 0.25, p: 0.8 }, { f: 6, a: 0.20, p: 3.2 },
    { f: 9, a: 0.15, p: 1.5 }, { f: 14, a: 0.10, p: 2.1 },
  ];
  let rxy = 0, rz = 0;
  for (const wv of waves) rxy += wv.a * Math.sin(omega * wv.f + wv.p);
  for (const wv of wZ) rz += wv.a * Math.sin(omega * wv.f + wv.p);
  x += rxy * rScale * Math.cos(omega);
  y += rxy * rScale * Math.sin(omega);
  z += rz * rScale * 0.4;
  return { x, y, z };
}

const ScatterCushion3DPreview = ({
  width = 50, height = 50,
  fabricColor, fabricImageUrl, fabricZoom = 1,
  showZip = true,
  rawWidth = 0, rawHeight = 0, unit = 'cm',
  activeField = null,
  pipingOption = 'none', pipingColor, pipingImageUrl
}) => {
  const mountRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const cushionRef = useRef(null);
  const zipRef = useRef(null);
  const dimLabelsRef = useRef({ width: null, height: null });
  const currentDimsRef = useRef(null);
  const targetDimsRef = useRef(null);
  const containerSizeRef = useRef({ w: 0, h: 0 });
  const initializedRef = useRef(false);
  const animFrameRef = useRef(null);
  const resetCameraRef = useRef(true);
  const showZipRef = useRef(showZip);
  const activeFieldRef = useRef(activeField);
  const pipingRingRef = useRef(null);

  useEffect(() => { showZipRef.current = showZip; }, [showZip]);
  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);

  useEffect(() => {
    if (!mountRef.current || initializedRef.current) return;
    initializedRef.current = true;
    const container = mountRef.current;
    const cw = container.clientWidth, ch = container.clientHeight;
    containerSizeRef.current = { w: cw, h: ch };

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f0eb);

    const camera = new THREE.PerspectiveCamera(35, cw / ch, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(cw, ch);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.panSpeed = 1.2;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    controls.target.set(0, 0, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dir1 = new THREE.DirectionalLight(0xffffff, 1.2);
    dir1.position.set(3, 5, 3);
    dir1.castShadow = true;
    dir1.shadow.mapSize.set(1024, 1024);
    scene.add(dir1);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));
    scene.add(new THREE.PointLight(0xffffff, 0.3).translateY(2));

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(5, 5),
      new THREE.ShadowMaterial({ opacity: 0.15 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.05;
    floor.receiveShadow = true;
    scene.add(floor);

    const w0 = width / NORM, h0 = height / NORM;
    const mat = new THREE.MeshStandardMaterial({ color: 0xc8b89a, roughness: 0.85, metalness: 0.02, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 });
    const mesh = new THREE.Mesh(buildPillowGeo(w0, h0), mat);
    mesh.castShadow = true;
    scene.add(mesh);
    cushionRef.current = mesh;

    currentDimsRef.current = { w: w0, h: h0 };
    targetDimsRef.current = { w: w0, h: h0 };

    // Zip on bottom edge - continuous tube + teeth following surface curve
    const zipTapeMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const zipTeethMat = new THREE.MeshBasicMaterial({ color: 0x888888 });
    const zipTube = new THREE.Mesh(new THREE.BufferGeometry(), zipTapeMat);
    scene.add(zipTube);
    const zipTeeth = Array.from({ length: 80 }, () => {
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.002, 0.003), zipTeethMat);
      scene.add(tooth);
      return tooth;
    });
    const zipPull = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.004, 0.004), zipTeethMat);
    scene.add(zipPull);
    zipRef.current = { tube: zipTube, teeth: zipTeeth, pull: zipPull };

    // Piping ring (full equatorial seam)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingRing = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    pipingRing.visible = false;
    scene.add(pipingRing);
    pipingRingRef.current = pipingRing;

    const targetCamPos = new THREE.Vector3();

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      controls.update();

      const cd = currentDimsRef.current;
      const td = targetDimsRef.current;
      if (!cd || !td) { renderer.render(scene, camera); return; }

      let needsRebuild = false;
      const next = {};
      for (const key of ['w', 'h']) {
        next[key] = THREE.MathUtils.lerp(cd[key], td[key], 0.1);
        if (Math.abs(next[key] - td[key]) > 0.0001) needsRebuild = true;
      }

      if (needsRebuild || true) {
        mesh.geometry.dispose();
        mesh.geometry = buildPillowGeo(next.w, next.h);
        currentDimsRef.current = next;
      }

      floor.position.y = -next.h / 2 - 0.02;

      const halfW = next.w / 2;
      const halfH = next.h / 2;
      const puffDepth = Math.min(next.w, next.h) * 0.32;

      // Zip - continuous tube along the full bottom edge
      const zr = zipRef.current;
      const vis = showZipRef.current;

      // Full bottom edge: corner to corner
      const omegaStart = -Math.PI * 0.74;
      const omegaEnd = -Math.PI * 0.26;
      const omegaRange = omegaEnd - omegaStart;

      // Build curve points for the tube - offset slightly forward to avoid z-fighting with seam
      const curvePoints = [];
      const numPts = 80;
      const zFwd = 0.008;
      for (let i = 0; i <= numPts; i++) {
        const t = i / numPts;
        const omega = omegaStart + t * omegaRange;
        const pos = bottomEdgePos(omega, next.w, next.h);
        curvePoints.push(new THREE.Vector3(pos.x, pos.y, pos.z + zFwd));
      }
      const curve = new THREE.CatmullRomCurve3(curvePoints, false);
      zr.tube.geometry.dispose();
      zr.tube.geometry = new THREE.TubeGeometry(curve, 64, 0.0025, 6, false);
      zr.tube.visible = vis;

      // Teeth positioned directly on the tube curve with minimal downward protrusion
      const tubeRadius = 0.0025;
      const toothHalfH = 0.001; // half of tooth BoxGeometry height (0.002)
      const toothDrop = tubeRadius + toothHalfH; // just enough to hang below the tube center
      for (let i = 0; i < zr.teeth.length; i++) {
        zr.teeth[i].visible = vis;
        // Sample position from the same curve the tube uses
        const t = (i + 0.5) / zr.teeth.length;
        const pt = curve.getPointAt(t);
        zr.teeth[i].position.set(pt.x, pt.y - toothDrop, pt.z);
      }

      // Pull tab at the right end of the zip
      zr.pull.visible = vis;
      const pullPt = curve.getPointAt(0.97);
      zr.pull.position.set(pullPt.x, pullPt.y - toothDrop, pullPt.z);

      // Labels
      const { w: cw2, h: ch2 } = containerSizeRef.current;
      const af = activeFieldRef.current;
      const activeBg = 'rgba(255,215,0,0.92)';
      const defaultBg = 'rgba(255,255,255,0.85)';
      const activeText = '#1a1a1a';
      const defaultText = '#374151';

      const project = (pos) => {
        const v = pos.clone().project(camera);
        return { x: (v.x * 0.5 + 0.5) * cw2, y: (-v.y * 0.5 + 0.5) * ch2 };
      };
      const setLabel = (el, isActive, worldPos) => {
        if (!el) return;
        const p = project(worldPos);
        el.style.left = `${p.x}px`;
        el.style.top = `${p.y}px`;
        el.style.opacity = '1';
        el.style.backgroundColor = isActive ? activeBg : defaultBg;
        el.style.color = isActive ? activeText : defaultText;
      };

      // Width label — at bottom center (closer to cushion)
      setLabel(dimLabelsRef.current.width, af === 'length',
        new THREE.Vector3(0, -halfH + 0.01, puffDepth / 2 + 0.02));
      // Height label — on the left side
      setLabel(dimLabelsRef.current.height, af === 'width',
        new THREE.Vector3(-halfW - 0.04, 0, 0));

      // Camera
      if (resetCameraRef.current) {
        const maxDim = Math.max(next.w, next.h);
        const isMobile = cw2 < 500;
        if (isMobile) {
          const dist = maxDim * 2.6 + 0.3;
          targetCamPos.set(0.03, 0.08, dist);
          camera.position.lerp(targetCamPos, 0.15);
          controls.target.set(-0.04, 0, 0);
        } else {
          // Desktop: exact user-tuned pose.
          targetCamPos.set(0.0539, 0.0607, 1.0813);
          camera.position.lerp(targetCamPos, 0.15);
          controls.target.set(-0.0117, -0.0077, -0.0012);
        }
        if (camera.position.distanceTo(targetCamPos) < 0.02) { resetCameraRef.current = false; controls.saveState(); }
      }

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const nw = container.clientWidth, nh = container.clientHeight;
      containerSizeRef.current = { w: nw, h: nh };
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', onResize);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      initializedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!targetDimsRef.current) return;
    targetDimsRef.current = { w: width / NORM, h: height / NORM };
    resetCameraRef.current = true;
  }, [width, height]);

  useEffect(() => {
    if (cushionRef.current) {
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(cushionRef.current, fabricImageUrl, fabricColor, fabricZoom, () => setFabricLoading(false));
    }
  }, [fabricColor, fabricImageUrl]);

  useEffect(() => {
    if (cushionRef.current) updateTextureZoom(cushionRef.current, fabricZoom);
  }, [fabricZoom]);

  // Update piping ring — trace full equatorial seam of the organic pillow shape
  useEffect(() => {
    const pr = pipingRingRef.current;
    if (!pr) return;

    const showPiping = pipingOption !== 'none';
    pr.visible = showPiping;
    if (!showPiping) { setPipingLoading(false); return; }

    const pColor = pipingOption === 'same' ? (fabricColor || '#c8b89a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    applyFabricTexture(pr, pImageUrl, pColor, fabricZoom, needsLoad ? () => setPipingLoading(false) : null);

    const w = width / NORM, h = height / NORM;
    const tubeRadius = 0.003;
    const numPts = 128;
    const expansion = 0.004; // slight outward push to sit on top of seam

    // Trace the full equatorial seam
    const seamPts = [];
    for (let i = 0; i < numPts; i++) {
      const omega = -Math.PI + (i / numPts) * 2 * Math.PI;
      const pos = bottomEdgePos(omega, w, h);
      // Expand slightly outward from center
      const len = Math.sqrt(pos.x * pos.x + pos.y * pos.y) || 1;
      const nx = pos.x / len;
      const ny = pos.y / len;
      seamPts.push(new THREE.Vector3(pos.x + nx * expansion, pos.y + ny * expansion, pos.z));
    }

    class LinearClosedCurve extends THREE.Curve {
      constructor(pts) { super(); this.pts = pts; }
      getPoint(t) {
        const n = this.pts.length;
        const f = t * n;
        const i = Math.floor(f) % n;
        const a = f - Math.floor(f);
        return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[(i + 1) % n], a);
      }
    }

    const curve = new LinearClosedCurve(seamPts);
    const tubeGeo = new THREE.TubeGeometry(curve, 192, tubeRadius, 8, true);

    pr.geometry.dispose();
    pr.geometry = tubeGeo;
  }, [width, height, fabricColor, pipingOption, pipingColor, pipingImageUrl, fabricImageUrl, fabricZoom]);

  const handleReset = () => { resetCameraRef.current = true; };

  return (
    <div className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }} data-testid="scatter-3d-preview">
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      <div ref={el => dimLabelsRef.current.width = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }} data-testid="dim-label-width">
        {rawWidth > 0 ? `${unit === 'mm' ? rawWidth * 10 : rawWidth} ${unit}` : `H ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.height = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }} data-testid="dim-label-height">
        {rawHeight > 0 ? `${unit === 'mm' ? rawHeight * 10 : rawHeight} ${unit}` : `H ${unit}`}
      </div>
      {showZip && (
        <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
          <svg width="24" height="6" viewBox="0 0 24 6"><line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" /></svg>
          <span className="font-medium">Zip placement</span>
        </div>
      )}
      <button onClick={handleReset} className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer" title="Reset view" data-testid="reset-view-btn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-15.36 6.36"/><path d="M3 12a9 9 0 0 1 15.36-6.36"/><polyline points="21 3 21 9 15 9"/><polyline points="3 21 3 15 9 15"/></svg>
      </button>
      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

export default ScatterCushion3DPreview;
